import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import PanelCard from '../../components/ui/PanelCard';
import Modal from '../../components/ui/Modal';
import { itemShort, ago } from '../user/routeUi';

/**
 * ── EGY FELHASZNÁLÓ ROUTE-JAI A STÁBNAK (terv 7.) ────────────────────────────
 *
 * KI MIT LÁT (a tulajdonos döntése, terv 7.4): a megosztott route-okat (élő kód
 * vagy könyvtár-tétel) az admin is, a PRIVÁT route-okat és szövegüket csak az
 * owner — a backend dönt (`privateVisible`), a felület csak kiírja.
 * Minden művelet az audit logba megy (a privát szöveg megnyitása is).
 */

export function useUserRoutes(userId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    api.get(`/admin/users/${userId}/routes`)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load the routes.'));
  }, [userId]);
  useEffect(() => { setData(null); load(); }, [load]);
  return { data, error, reload: load };
}

export function UserRoutesCard({ state }) {
  const { data, error, reload } = state;
  const [filter, setFilter] = useState('all');
  const [textOf, setTextOf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const act = async (fn, ok) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ type: 'ok', text: ok }); reload(); }
    catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Something went wrong.' }); }
    finally { setBusy(false); }
  };

  const items = (data?.items || []).filter(r =>
    filter === 'shared' ? (r.share || r.library)
      : filter === 'hidden' ? r.library?.status === 'hidden'
        : true);

  return (
    <PanelCard
      title="Routes"
      subtitle={data?.privateVisible
        ? "This account's cloud routes. Private routes are shown to the owner only."
        : 'Only the routes this account shares (share code or route library). Private routes are visible to the owner only.'}
      flush
      actions={data?.privateVisible ? (
        <div className="segf" role="group" aria-label="Filter routes">
          {[['all', 'All'], ['shared', 'Shared'], ['hidden', 'Hidden']].map(([id, label]) => (
            <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}>
              {label}
            </button>
          ))}
        </div>
      ) : null}
    >
      {error ? <p className="px-5 py-4 text-sm text-[#ff8a80]">{error}</p>
        : !data ? <p className="px-5 py-4 text-sm text-gray-500">Loading…</p>
          : items.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-500">
              {data.privateVisible ? 'No routes.' : 'This account shares no routes.'}
            </p>
          ) : (
            <div>
              {msg && <p className={`px-5 py-2 text-[13px] ${msg.type === 'ok' ? 'text-ok' : 'text-[#ff8a80]'}`}>{msg.text}</p>}
              {items.map(r => (
                <div key={r.id} className="list-row px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="min-w-0 flex-1 basis-60">
                    <b className="block text-sm font-medium text-gray-50 break-words">{r.key}</b>
                    <div className="mt-[3px] text-[12.5px] text-gray-500">
                      {r.summary?.steps ?? 0} {r.summary?.steps === 1 ? 'step' : 'steps'}
                      {itemShort(r.summary?.items) && <span className="text-warn"> · uses {itemShort(r.summary.items)}</span>}
                      {r.source && ` · from ${r.source.user}`}
                      {` · changed ${ago(r.changedAt)}`}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    {r.library && (
                      <span className={r.library.status === 'hidden' ? 'badge-banned' : 'badge-brand'}>
                        Library · {r.library.status === 'hidden' ? `hidden${r.library.hiddenBy ? ` by ${r.library.hiddenBy}` : ''}` : r.library.status === 'live' ? `${r.library.adds} ${r.library.adds === 1 ? 'add' : 'adds'}` : r.library.status}
                      </span>
                    )}
                    {r.library?.reports > 0 && <span className="badge-warn">{r.library.reports} {r.library.reports === 1 ? 'report' : 'reports'}</span>}
                    {r.share && (
                      <>
                        <span className="badge-brand">Code {r.share.code}</span>
                        <span className="text-[12px] text-gray-500">{r.share.uses} redeemed</span>
                      </>
                    )}
                    {!r.share && !r.library && <span className="badge-inactive">Private</span>}
                  </div>
                  <div className="flex gap-1.5 ml-auto">
                    <button type="button" className="btn-glass btn-sm" onClick={() => setTextOf(r)}>View text</button>
                    {r.share && (
                      <button type="button" className="btn-glass btn-sm" disabled={busy}
                              onClick={() => act(() => api.post(`/admin/route-shares/${r.share.id}/disable`, {}), `Code ${r.share.code} disabled.`)}>
                        Disable code
                      </button>
                    )}
                    {r.library?.status === 'live' && (
                      <button type="button" className="btn-glass btn-sm" disabled={busy}
                              onClick={() => act(() => api.post(`/library/review/${r.library.id}/hide`, {}), 'Listing hidden.')}>
                        Hide listing
                      </button>
                    )}
                    {r.library?.status === 'hidden' && (
                      <button type="button" className="btn-glass btn-sm" disabled={busy}
                              onClick={() => act(() => api.post(`/library/review/${r.library.id}/unhide`), 'Listing visible again.')}>
                        Unhide
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <p className="px-5 py-3 text-[12.5px] text-gray-500">Every hide, code disable and block is written to the audit log with your name, and so is opening a private route's text.</p>
            </div>
          )}
      {textOf && <RouteTextModal route={textOf} onClose={() => setTextOf(null)} />}
    </PanelCard>
  );
}

function RouteTextModal({ route, onClose }) {
  const [text, setText] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/admin/routes/${route.id}/text`, { responseType: 'text', transformResponse: x => x })
      .then(r => setText(r.data))
      .catch(err => {
        let e = 'Failed to load the route text.';
        try { e = JSON.parse(err.response?.data)?.error || e; } catch { /* nem JSON */ }
        setError(e);
      });
  }, [route.id]);
  return (
    <Modal title="Route text" subtitle={route.key} size="2xl" onClose={onClose}>
      {error ? <p className="text-sm text-[#ff8a80]">{error}</p>
        : text === null ? <p className="text-sm text-gray-500">Loading…</p>
          : <pre className="surface-sunken p-3.5 text-[12px] leading-[1.75] text-gray-100 font-mono max-h-[60vh] overflow-auto whitespace-pre">{text}</pre>}
    </Modal>
  );
}

export function RouteSharingCard({ state, userId }) {
  const { data, reload } = state;
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!data) return null;
  const t = data.totals;
  const s = data.sharing;

  const setBlocked = async (blocked) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/admin/users/${userId}/route-sharing`, { blocked, reason: blocked ? reason : undefined });
      setAsking(false); setReason('');
      reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setBusy(false); }
  };

  const Row = ({ label, children }) => (
    <div className="flex justify-between items-start gap-3 py-2 border-b border-line-faint last:border-0 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-50 text-right">{children}</span>
    </div>
  );

  return (
    <PanelCard title="Route sharing">
      <div className="flex flex-col gap-4">
        <div className="surface-sunken px-3.5">
          <Row label="Routes">{t.routes} / {t.max}</Row>
          <Row label="Library listings">{t.listings}{t.hidden ? ` · ${t.hidden} hidden` : ''}</Row>
          <Row label="Share codes">{t.codesActive} active · {t.codesRevoked} revoked</Row>
          <Row label="Redeemed by others">{t.redeemed}</Row>
          <Row label="Open reports"><span className={t.openReports ? 'text-warn' : ''}>{t.openReports}</span></Row>
          <Row label="Last change">{t.lastChange ? ago(t.lastChange) : '—'}</Row>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-sm text-gray-100">Publishing</span>
            <span className={`text-[12.5px] ${s.blocked ? 'text-[#ff8a80]' : 'text-ok'}`}>
              {s.blocked ? `Blocked${s.blockedBy ? ` by ${s.blockedBy}` : ''}${s.reason ? `: ${s.reason}` : ''}` : 'Allowed'}
            </span>
          </div>
          {s.blocked ? (
            <button type="button" className="btn-glass btn-sm" disabled={busy} onClick={() => setBlocked(false)}>Allow publishing</button>
          ) : (
            <button type="button" className="btn-danger btn-sm" disabled={busy} onClick={() => setAsking(true)}>Block publishing</button>
          )}
        </div>
        <span className="text-[12.5px] text-gray-500">
          Blocks new library submissions and new share codes for this account. What is already out stays until you hide it.
        </span>
        {error && <p className="text-[13px] text-[#ff8a80]">{error}</p>}
      </div>
      {asking && (
        <Modal title="Block publishing?" tone="danger" onClose={() => setAsking(false)}
               footer={
                 <div className="flex justify-end gap-2">
                   <button type="button" className="btn-ghost" onClick={() => setAsking(false)}>Cancel</button>
                   <button type="button" className="btn-danger" disabled={busy} onClick={() => setBlocked(true)}>Block publishing</button>
                 </div>
               }>
          <div className="flex flex-col gap-2">
            <label htmlFor="blk-reason" className="label mb-0">Reason (optional, staff only)</label>
            <input id="blk-reason" className="input" maxLength={255} value={reason} onChange={e => setReason(e.target.value)} />
          </div>
        </Modal>
      )}
    </PanelCard>
  );
}
